import type { HierarcicalObjectReference, ObjectId } from "@novorender/data-js-api";

import { byProperty, type ObjectDataAccess, type PropertyQuery } from "dataAccess";

import { getFilePathFromObjectPath, isLeafNode } from "./objectData";
import { getFileExtensionFromPath } from "./objectPath";
import { SelectionSet } from "./selectionSet";

/**
 * Extension-less federated 2D files: Drawings-tool sheets (`drawings`) and
 * Documents-tool files (`DOCUMENTS`). Object-db equality is case-sensitive, so
 * both casings seen in scenes are listed; the per-object fallback is case-insensitive.
 */
const DOCUMENT_SOURCE_QUERY: PropertyQuery = byProperty("Procore/DocumentSource", [
    "drawings",
    "DOCUMENTS",
    "documents",
]);

/** Sheets whose path still carries a `.pdf` extension — a substring, so `contains`. */
const PDF_PATH_QUERY: PropertyQuery = { terms: [{ property: "path", contains: ".pdf" }] };

/**
 * PDF/drawing sheet ids for the current scene.
 *
 * Procore does not nest 2D files in folders, so two scene-wide queries are enough: one
 * on `Procore/DocumentSource`, one on path for `.pdf`. They run in parallel and are
 * merged; consumers never observe a partial set.
 *
 * Each query matches on its own terms, so hits are kept only when the path also satisfies
 * the rule `isPdfOrDrawingObjectId` (`dataAccess/utils`) applies: the set gates 3D
 * selection, and an id in it is never read again.
 *
 * **Known gap (BM-13779).** {@link pathAllowsDocument} admits only nodes reported as
 * leaves, and the federated file roots in the investigated scenes are `NodeType.Internal`
 * carrying no `descendants` — 2D document and extension-less 3D container alike. Those ids
 * stay out of this set, so the reset flyout keeps counting them, the object tree keeps
 * their row and its partially-hidden hint, and a canvas pick still resolves them as 3D.
 * Only leaf-typed document nodes are covered; see the gap tests in
 * `features/modelTree/__tests__/utils.test.ts`.
 *
 * Rejects on an abort rather than answering short, against the data-access contract of
 * answering one with an empty value: a silently truncated set reads as "these are all the
 * sheets", and every id missing from it turns a drawing click into a 3D pick. Hence the
 * re-check after the queries — an aborted iterator simply stops yielding.
 */
export async function discoverPdfObjectIds(access: ObjectDataAccess, abortSignal?: AbortSignal): Promise<SelectionSet> {
    abortSignal?.throwIfAborted();
    const [fromSource, fromPath] = await Promise.all([
        collectMatchingIds(access, DOCUMENT_SOURCE_QUERY, pathAllowsDocument, abortSignal),
        collectMatchingIds(access, PDF_PATH_QUERY, hasPdfFilePath, abortSignal),
    ]);
    abortSignal?.throwIfAborted();
    return fromSource.withAdded(fromPath);
}

export const PDF_DISCOVERY_TIMEOUT_MS = 30_000;

/**
 * @param timeoutMs Give up after this long; `null` waits until discovery settles. Defaults to
 * {@link PDF_DISCOVERY_TIMEOUT_MS}.
 * @returns The sheet ids, or `undefined` if discovery fails, takes longer than `timeoutMs`, or
 * `abortSignal` fires.
 */
export async function waitForPdfObjectIds(
    promise: Promise<SelectionSet>,
    abortSignal?: AbortSignal,
    timeoutMs: number | null = PDF_DISCOVERY_TIMEOUT_MS,
): Promise<SelectionSet | undefined> {
    const timeout = timeoutMs === null ? undefined : AbortSignal.timeout(timeoutMs);
    const signals = [abortSignal, timeout].filter((s): s is AbortSignal => s !== undefined);
    if (!signals.length) {
        try {
            return await promise;
        } catch {
            return undefined;
        }
    }
    const signal = AbortSignal.any(signals);
    if (signal.aborted) {
        return undefined;
    }
    // Resolves rather than rejects: once `promise` wins the race, a later abort would go unhandled.
    const aborted = new Promise<undefined>((resolve) => {
        signal.addEventListener("abort", () => resolve(undefined), { once: true });
    });
    try {
        return await Promise.race([promise, aborted]);
    } catch {
        return undefined;
    }
}

/** Sheet ids as discovery stands: `ids` once known, `promise` while discovery is outstanding. */
export interface PdfObjectIdsSource {
    ids: SelectionSet | undefined;
    promise: Promise<SelectionSet> | undefined;
}

/** The sheet ids, waiting for outstanding discovery as {@link waitForPdfObjectIds} does. */
export async function awaitPdfObjectIds(
    { ids, promise }: PdfObjectIdsSource,
    abortSignal?: AbortSignal,
    timeoutMs?: number | null,
): Promise<SelectionSet | undefined> {
    return ids ?? (promise ? await waitForPdfObjectIds(promise, abortSignal, timeoutMs) : undefined);
}

const sheetsToShowOnDiscovery = new WeakMap<Promise<SelectionSet>, readonly ObjectId[]>();

/**
 * Records the sheets to show once `discovery` resolves, for a viewpoint or reset applied before
 * it did; every other sheet is hidden then. Replaces any earlier record for `discovery`.
 */
export function showSheetsOnDiscovery(discovery: Promise<SelectionSet>, visibleSheetIds: readonly ObjectId[]) {
    sheetsToShowOnDiscovery.set(discovery, visibleSheetIds);
}

/** @returns The sheets recorded by {@link showSheetsOnDiscovery}, or `undefined` if none were. */
export function getSheetsToShowOnDiscovery(discovery: Promise<SelectionSet>): readonly ObjectId[] | undefined {
    return sheetsToShowOnDiscovery.get(discovery);
}

/** The file the path names, or `null` when it names none — the extension-less federated case. */
function filePathOf(object: HierarcicalObjectReference): string | null {
    return object.path ? getFilePathFromObjectPath(object.path) : null;
}

/**
 * `DocumentSource` only decides for leaf paths that name no file (or a `.pdf`); a hit
 * whose path names a non-PDF file is not a document.
 *
 * The `isLeafNode` gate is meant to keep a federated model root that carries the stamp out
 * of the set, but it is keyed on node *type* rather than on having children, so it also
 * rejects the Internal, childless document roots of BM-13779. A model whose path names its
 * format (`.nwd`, `.ifc`, …) is already excluded by the path rule below, so the gate only
 * changes the answer for the extension-less hash case — where a 2D document and a 3D
 * container are, on every property we can read, the same node.
 */
function pathAllowsDocument(object: HierarcicalObjectReference): boolean {
    if (!isLeafNode(object)) {
        return false;
    }
    const filePath = filePathOf(object);
    return filePath === null || isPdfFilePath(filePath);
}

/** The `.pdf` is the file itself, not a substring of some folder along the way. */
function hasPdfFilePath(object: HierarcicalObjectReference): boolean {
    const filePath = filePathOf(object);
    return filePath !== null && isPdfFilePath(filePath);
}

/** `plan.pdf (2)` is still a PDF; the disambiguation suffix is not part of the extension. */
function isPdfFilePath(filePath: string): boolean {
    return getFileExtensionFromPath(filePath) === "pdf";
}

async function collectMatchingIds(
    access: ObjectDataAccess,
    query: PropertyQuery,
    filter: (object: HierarcicalObjectReference) => boolean,
    abortSignal: AbortSignal | undefined,
): Promise<SelectionSet> {
    const ids = new Set<number>();
    for await (const hit of access.findByProperties(query, { signal: abortSignal })) {
        abortSignal?.throwIfAborted();
        if (filter(hit)) {
            ids.add(hit.id);
        }
    }
    return SelectionSet.fromIds(ids);
}
